import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertCircle,
  Download,
  Loader2,
  PackageSearch,
  RefreshCw,
  Search,
  X,
} from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { TooltipProvider } from '@/components/ui/tooltip'
import { STATUS_LABEL } from '@/components/status-pill'
import { channelLabel } from '@/components/channel-tag'
import { OrdersTable, sortOrderRows } from './orders-table'
import { Pager } from './pager'
import { MultiScopeFilter, type ScopeOption } from './scope-filter'
import { MultiChannelFilter } from './channel-filter'
import { StatusFilter } from './status-filter'
import { BcSalesOrderFilterChips, BcSalesOrderFilterControl } from './bc-so-filter'
import { DateFilterControl } from './date-filter'
import { dateBounds } from './date-range'
import { orderDetailHref, useOrdersUrlState, visionSortParam } from './url-state'
import { useOrdersQuery } from './queries'
import { PoListSummary } from './po-list-summary'
import { useChannelsQuery, useVendorsQuery } from '@/features/directory/queries'
import { vendorFilterOptions, vendorLabel } from './vendor-label'
import { poListFulfilment } from './po-list-fulfilment'
import { useVisionClient } from '@/app/vision-client-provider'
import { useCanWriteOms } from '@/app/auth/use-pulse-me'
import { useTenantStore } from '@/app/state/tenant-store'
import { downloadCsv, stampName, type CsvCol } from '@/lib/csv'
import { OMS_WRITES_ENABLED } from '@/lib/env'
import { fullDate, num } from '@/lib/format'
import type { Order } from '@/lib/vision/schemas'
import { BulkAckDialog } from './bulk-ack-dialog'
import {
  MAX_BULK_ACK_POS,
  PENDING_ACK_STATUSES,
  bulkAckDisableLabel,
  bulkAckDisableReason,
  isBulkAckEligible,
  normalizeAckChannel,
  pageSelectAllState,
} from './bulk-ack-eligibility'
import { BulkAckSelectionBar } from './bulk-ack-selection-bar'
import { useBulkAckSelectionStore } from './bulk-ack-selection-store'
import { friendlyQueueError } from './queue-error'

const CSV_PAGE_SIZE = 200
const MATCH_PAGE_SIZE = 100

export function OrdersRoute() {
  const [state, update] = useOrdersUrlState()
  const [qLive, setQLive] = useState(state.q)
  const tenantId = useTenantStore(s => s.tenantId)
  const client = useVisionClient()
  const navigate = useNavigate()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [exporting, setExporting] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [selectingMatching, setSelectingMatching] = useState(false)
  const [bulkAckOpen, setBulkAckOpen] = useState(false)
  const queryClient = useQueryClient()
  const canWriteOmsRole = useCanWriteOms()
  const writesAllowed = OMS_WRITES_ENABLED && canWriteOmsRole
  const selectedOrders = useBulkAckSelectionStore(s => s.orders)
  const toggleSelection = useBulkAckSelectionStore(s => s.toggle)
  const selectMany = useBulkAckSelectionStore(s => s.selectMany)
  const removeMany = useBulkAckSelectionStore(s => s.removeMany)
  const clearSelection = useBulkAckSelectionStore(s => s.clear)
  const keepTenant = useBulkAckSelectionStore(s => s.keepTenant)
  const clearIfChannelFilterChanged = useBulkAckSelectionStore(s => s.clearIfChannelFilterChanged)

  useEffect(() => {
    setQLive(state.q)
  }, [state.q])

  useEffect(() => {
    keepTenant(tenantId)
  }, [keepTenant, tenantId])

  useEffect(() => {
    clearIfChannelFilterChanged(state.channels, state.channelCode)
  }, [clearIfChannelFilterChanged, state.channelCode, state.channels])

  // Debounce the free-text search — server round-trip only after typing stills.
  useEffect(() => {
    if (qLive === state.q) return
    const timer = window.setTimeout(() => {
      update({ q: qLive || null })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [qLive, state.q, update])

  const channelsQuery = useChannelsQuery(tenantId)
  // A channel filter of any kind scopes the vendor dropdown to vendors that
  // actually have POs on any selected channel family (or the legacy specific
  // channel-account slug carried by a deep link).
  const channelScoped = state.channels.length > 0 || Boolean(state.channelCode)
  const vendorsQuery = useVendorsQuery(tenantId, {
    channel: state.channels.length ? state.channels : null,
    channelCode: state.channelCode,
  })

  const vendors = useMemo(() => vendorsQuery.data ?? [], [vendorsQuery.data])

  const dateWindow = dateBounds(state.date)
  const channelFilter = state.channels.length ? state.channels : undefined
  const query = useOrdersQuery({
    channel: channelFilter,
    channelCode: state.channelCode,
    vendorCode: state.vendorCodes.length ? state.vendorCodes : undefined,
    status: state.statuses.length ? state.statuses : undefined,
    bcSalesOrder: state.bcSalesOrder.length ? state.bcSalesOrder : undefined,
    q: state.q || undefined,
    from: dateWindow.from,
    to: dateWindow.to,
    page: state.page,
    size: state.size,
    sort: visionSortParam(state.sort),
  })

  const rows = useMemo(
    () => sortOrderRows(query.data?.items ?? [], state.sort),
    [query.data, state.sort],
  )
  // Scoped: the server list is the whole truth, so `rows` does not get unioned
  // in. `useOrdersQuery` keeps the previous page via `placeholderData`, so
  // right after a channel change `rows` still holds the *old* channel's orders
  // — unioning them would inject foreign vendors and make a now-invalid
  // selection look valid. Nothing is lost: the server list is `SELECT DISTINCT
  // vendor` over the same rows under the same predicate, a strict superset of
  // anything `rows` could contribute.
  const vendorOptions: ScopeOption[] = useMemo(
    () => (channelScoped ? vendorFilterOptions(vendors) : vendorFilterOptions(vendors, rows)),
    [channelScoped, vendors, rows],
  )
  const total = query.data?.total ?? 0

  // Switching to a channel the selected vendor doesn't trade on drops that
  // vendor out of the dropdown, which would strand the operator: the queue
  // filters to zero rows and the offending selection is no longer there to
  // unpick. Clear it here rather than in the channel dropdown's onChange —
  // eagerly clearing would also throw away vendors that are still valid on the
  // new channel, and would do nothing for a deep link or a refresh, which
  // never fire onChange.
  //
  // Only act on a settled successful fetch: an in-flight or failed vendors
  // query has no opinion about which vendors exist, and clearing on either
  // would eat a valid deep link or a valid selection mid-refetch. Compare
  // case-insensitively — Vision lowercases `vendorCode` in SQL, so a
  // hand-edited link may not match the directory's casing.
  const validVendorCodes = useMemo(() => {
    if (!channelScoped || !vendorsQuery.isSuccess || vendorsQuery.isFetching) {
      return state.vendorCodes
    }
    const available = new Set(vendorOptions.map(option => option.code.toLowerCase()))
    return state.vendorCodes.filter(code => available.has(code.toLowerCase()))
  }, [
    channelScoped,
    state.vendorCodes,
    vendorOptions,
    vendorsQuery.isFetching,
    vendorsQuery.isSuccess,
  ])
  const hasStaleVendorSelections = validVendorCodes.length !== state.vendorCodes.length
  useEffect(() => {
    // `update()` deletes `page` on any patch that doesn't carry one, so the
    // orders query re-runs from page 0 without only the invalid vendors.
    if (hasStaleVendorSelections) {
      update({ vendorCodes: validVendorCodes.length ? validVendorCodes : null })
    }
  }, [hasStaleVendorSelections, update, validVendorCodes])

  // -1 until the operator reaches for the arrow keys: the queue opens with no
  // row pre-highlighted. A fresh page resets the cursor the same way.
  const [focusedIndex, setFocusedIndex] = useState(-1)
  useEffect(() => {
    setFocusedIndex(-1)
  }, [rows])

  const onFocusRow = useCallback((index: number) => setFocusedIndex(index), [])

  // Global keyboard shortcuts for the queue. `/` focuses the search; arrows
  // walk the visible rows; Enter opens the focused row's detail. `?` is
  // reserved for a future help modal.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement | null
      const editing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.getAttribute('contenteditable') === 'true'
      if (event.key === '/' && !editing) {
        event.preventDefault()
        searchInputRef.current?.focus()
        searchInputRef.current?.select()
        return
      }
      if (editing) return
      if (event.key === 'ArrowDown' || event.key === 'j') {
        if (rows.length === 0) return
        event.preventDefault()
        setFocusedIndex(prev => Math.min(rows.length - 1, prev + 1))
      } else if (event.key === 'ArrowUp' || event.key === 'k') {
        if (rows.length === 0) return
        event.preventDefault()
        setFocusedIndex(prev => Math.max(0, prev - 1))
      } else if (event.key === 'Enter') {
        const focused = focusedIndex >= 0 ? rows[focusedIndex] : undefined
        if (focused) {
          event.preventDefault()
          navigate(
            orderDetailHref(focused.externalOrderNumber, focused.channel || state.channels[0]),
          )
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [rows, focusedIndex, navigate, state.channels])

  const hasFilters =
    Boolean(state.q) ||
    state.channels.length > 0 ||
    state.statuses.length > 0 ||
    state.bcSalesOrder.length > 0 ||
    state.channelCode != null ||
    state.vendorCodes.length > 0 ||
    state.date.preset !== 'all'
  const clearFilters = () =>
    update({
      q: null,
      statuses: null,
      bcSalesOrder: null,
      channels: null,
      channelCode: null,
      vendorCodes: null,
      date: null,
    })

  const onExport = useCallback(async () => {
    if (exporting) return
    setExporting(true)
    try {
      const collected: Order[] = []
      let page = 0
      // Walk the pages with the current filters — the server owns the
      // ordering, so pagination gives us exactly what the operator sees
      // in the queue, just without the display cap.
      // The loop guards against a total of 0 (empty result) and against a
      // pathological "next page also has 0 items" reply.
      while (true) {
        const chunk = await client.listOrders({
          channel: channelFilter,
          channelCode: state.channelCode,
          vendorCode: state.vendorCodes.length ? state.vendorCodes : undefined,
          status: state.statuses.length ? state.statuses : undefined,
          bcSalesOrder: state.bcSalesOrder.length ? state.bcSalesOrder : undefined,
          q: state.q || undefined,
          from: dateWindow.from,
          to: dateWindow.to,
          page,
          size: CSV_PAGE_SIZE,
          sort: visionSortParam(state.sort),
        })
        collected.push(...chunk.items)
        page += 1
        if (chunk.items.length === 0) break
        if (collected.length >= chunk.total) break
      }
      if (collected.length === 0) {
        toast.info('Nothing to export', {
          description: 'The current filters have no matching POs.',
        })
        return
      }
      downloadCsv(stampName('pulse-pos'), collected, CSV_COLUMNS)
      toast.success(`Downloaded ${num(collected.length)} POs`)
    } catch (error) {
      toast.error('Export failed', {
        description: friendlyQueueError(error),
      })
    } finally {
      setExporting(false)
    }
  }, [
    client,
    exporting,
    channelFilter,
    state.channelCode,
    state.vendorCodes,
    state.q,
    state.sort,
    state.statuses,
    state.bcSalesOrder,
    dateWindow.from,
    dateWindow.to,
  ])

  const onRefresh = useCallback(async () => {
    if (refreshing) return
    setRefreshing(true)
    try {
      await Promise.all([
        query.refetch(),
        queryClient.refetchQueries({
          queryKey: ['orders-summary', tenantId],
          type: 'active',
        }),
      ])
    } finally {
      setRefreshing(false)
    }
  }, [query, queryClient, refreshing, tenantId])

  const selectedIds = useMemo(
    () => new Set(selectedOrders.map(order => order.id)),
    [selectedOrders],
  )
  const selectedChannel = selectedOrders[0]?.channel ?? null
  const selectionOpts = useMemo(
    () => ({
      selectedIds,
      selectedChannel,
      selectedCount: selectedOrders.length,
      writesAllowed,
    }),
    [selectedChannel, selectedIds, selectedOrders.length, writesAllowed],
  )
  const pageSelection = useMemo(
    () => pageSelectAllState(rows, selectedIds, selectedChannel, selectedOrders.length, writesAllowed),
    [rows, selectedChannel, selectedIds, selectedOrders.length, writesAllowed],
  )
  const pendingPresetActive =
    PENDING_ACK_STATUSES.every(status => state.statuses.includes(status)) &&
    state.statuses.length === PENDING_ACK_STATUSES.length

  const onToggleSelection = useCallback(
    (order: Order) => {
      const already = selectedIds.has(order.id)
      if (!already) {
        const reason = bulkAckDisableReason(order, selectionOpts)
        if (reason) {
          toast.error(bulkAckDisableLabel(reason, selectedChannel) ?? 'Cannot select this PO', {
            id: 'bulk-ack-select-blocked',
          })
          return
        }
      }
      toggleSelection(order)
    },
    [selectedChannel, selectedIds, selectionOpts, toggleSelection],
  )

  const onTogglePageSelection = useCallback(() => {
    if (pageSelection.targets.length === 0) return
    const allSelected = pageSelection.targets.every(order => selectedIds.has(order.id))
    if (allSelected) {
      removeMany(pageSelection.targets.map(order => order.id))
      return
    }
    selectMany(pageSelection.targets)
  }, [pageSelection.targets, removeMany, selectMany, selectedIds])

  const onSelectMatching = useCallback(async () => {
    if (selectingMatching || !writesAllowed) return
    setSelectingMatching(true)
    try {
      const batchChannel = selectedChannel
      const collected: Order[] = []
      let page = 0
      while (collected.length < MAX_BULK_ACK_POS) {
        const chunk = await client.listOrders({
          channel: channelFilter,
          channelCode: state.channelCode,
          vendorCode: state.vendorCodes.length ? state.vendorCodes : undefined,
          status: state.statuses.length ? state.statuses : undefined,
          bcSalesOrder: state.bcSalesOrder.length ? state.bcSalesOrder : undefined,
          q: state.q || undefined,
          from: dateWindow.from,
          to: dateWindow.to,
          page,
          size: MATCH_PAGE_SIZE,
          sort: visionSortParam(state.sort),
        })
        for (const order of chunk.items) {
          if (!isBulkAckEligible(order)) continue
          if (
            batchChannel &&
            normalizeAckChannel(order.channel) !== normalizeAckChannel(batchChannel)
          ) {
            continue
          }
          if (
            !batchChannel &&
            collected[0] &&
            normalizeAckChannel(order.channel) !== normalizeAckChannel(collected[0].channel)
          ) {
            continue
          }
          if (collected.some(row => row.id === order.id) || selectedIds.has(order.id)) continue
          collected.push(order)
          if (collected.length + selectedOrders.length >= MAX_BULK_ACK_POS) break
        }
        page += 1
        if (chunk.items.length === 0) break
        if (page * MATCH_PAGE_SIZE >= chunk.total) break
        if (collected.length + selectedOrders.length >= MAX_BULK_ACK_POS) break
      }
      const result = selectMany(collected)
      if (result.added === 0) {
        toast.info('No additional pending Amazon POs match the current filters')
      } else if (result.capped || collected.length + selectedOrders.length >= MAX_BULK_ACK_POS) {
        toast.message(`Selected up to ${MAX_BULK_ACK_POS} POs`, {
          description: 'Clear some POs to include more from this filter.',
        })
      }
    } catch (error) {
      toast.error('Could not select matching POs', {
        description: friendlyQueueError(error),
      })
    } finally {
      setSelectingMatching(false)
    }
  }, [
    channelFilter,
    client,
    dateWindow.from,
    dateWindow.to,
    selectMany,
    selectedChannel,
    selectedIds,
    selectedOrders.length,
    selectingMatching,
    state.bcSalesOrder,
    state.channelCode,
    state.q,
    state.sort,
    state.statuses,
    state.vendorCodes,
    writesAllowed,
  ])

  return (
    <TooltipProvider delayDuration={200}>
      <section className="flex flex-col gap-4 pb-20">
        {/* One row: the metric cards, then the queue-level actions pinned to the
            right edge. Upload is not here — it is a page of its own in the top nav. */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <PoListSummary />
          {/* Phones: the actions share the row edge to edge. */}
          <div className="grid grid-flow-col auto-cols-fr gap-2 md:flex md:shrink-0 md:items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              disabled={exporting || query.isLoading}
              aria-label="Download the current queue as CSV"
            >
              {exporting ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Download className="size-3.5" />
              )}
              {exporting ? 'Downloading…' : 'Download CSV'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing || query.isFetching}
              aria-label="Refresh queue"
            >
              <RefreshCw
                className={refreshing || query.isFetching ? 'size-3.5 animate-spin' : 'size-3.5'}
              />
              Refresh
            </Button>
          </div>
        </div>

        {/* Phones: a two-column grid so the four filters line up in pairs
            with the search and the date pills spanning the row. */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <label className="relative col-span-2 w-full min-w-0 sm:col-auto sm:w-auto">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-[color:var(--muted-foreground)]"
              aria-hidden
            />
            <Input
              ref={searchInputRef}
              value={qLive}
              onChange={event => setQLive(event.target.value)}
              placeholder="Search PO, ASIN, FSN…"
              className="h-8 max-md:min-h-11 w-full sm:w-64 pl-8"
              aria-label="Search orders by PO number, ASIN, or FSN"
            />
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10.5px] text-[color:var(--muted-foreground)]">
              /
            </span>
          </label>
          <StatusFilter
            value={state.statuses}
            onChange={statuses => update({ statuses: statuses.length ? statuses : null })}
          />
          <Button
            type="button"
            variant={pendingPresetActive ? 'default' : 'outline'}
            size="sm"
            aria-pressed={pendingPresetActive}
            onClick={() =>
              update({
                statuses: pendingPresetActive ? null : [...PENDING_ACK_STATUSES],
              })
            }
          >
            Pending acknowledgement
          </Button>
          <BcSalesOrderFilterControl
            value={state.bcSalesOrder}
            onChange={next => update({ bcSalesOrder: next.length ? next : null })}
          />
          <MultiChannelFilter
            value={state.channels}
            channels={channelsQuery.data ?? []}
            loading={channelsQuery.isLoading}
            error={channelsQuery.isError}
            onChange={channels => {
              clearSelection()
              update({ channels: channels.length ? channels : null, channelCode: null })
            }}
          />
          <MultiScopeFilter
            label="Vendor"
            ariaLabel="Filter orders by vendor"
            emptyLabel="No vendors configured for this tenant"
            value={state.vendorCodes}
            options={vendorOptions}
            loading={vendorsQuery.isLoading}
            error={vendorsQuery.isError}
            onChange={vendorCodes =>
              update({ vendorCodes: vendorCodes.length ? vendorCodes : null })
            }
          />
          {hasFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="col-span-2 sm:col-auto"
            >
              <X className="mr-1 size-3.5" /> Clear
            </Button>
          )}
          {(state.statuses.length > 0 || state.bcSalesOrder.length > 0) && (
            <div className="col-span-2 flex flex-wrap items-center gap-1 sm:col-auto sm:ml-1">
              {state.statuses.map(status => (
                <Badge key={status} tone="info">
                  {STATUS_LABEL[status]}
                </Badge>
              ))}
              <BcSalesOrderFilterChips value={state.bcSalesOrder} />
            </div>
          )}
          {/* Time-filter slot (workstream 1). Do not add qty columns or the
              POs / Ordered / Sent / Pending summary strip here. */}
          <DateFilterControl
            className="col-span-2 sm:col-auto sm:ml-auto"
            value={state.date}
            onChange={date => update({ date })}
          />
        </div>

        {query.isError && !query.data && (
          <ErrorPanel error={query.error} onRetry={() => query.refetch()} />
        )}

        <OrdersTable
          rows={rows}
          vendors={vendors}
          loading={query.isLoading}
          focusedIndex={focusedIndex}
          onFocusRow={onFocusRow}
          sort={state.sort}
          onSort={sort => update({ sort })}
          selectedOrderIds={writesAllowed ? selectedIds : undefined}
          onToggleSelection={writesAllowed ? onToggleSelection : undefined}
          isSelectable={order => bulkAckDisableReason(order, selectionOpts) == null}
          selectionDisabledReason={order =>
            bulkAckDisableLabel(bulkAckDisableReason(order, selectionOpts), selectedChannel)
          }
          pageSelectionChecked={pageSelection.checked}
          pageSelectionDisabled={pageSelection.disabled}
          pageSelectionTitle={pageSelection.title}
          onTogglePageSelection={writesAllowed ? onTogglePageSelection : undefined}
          emptyState={
            hasFilters ? (
              <EmptyFiltered onClear={clearFilters} />
            ) : (
              <EmptyQueue channelScoped={state.channels.length > 0} />
            )
          }
        />

        <Pager
          page={state.page}
          size={state.size}
          total={total}
          onPageChange={page => update({ page: page === 0 ? null : page })}
          onSizeChange={size => update({ size: size === 25 ? null : size, page: null })}
        />

        {writesAllowed ? (
          <BulkAckSelectionBar
            selectedCount={selectedOrders.length}
            matchingTotal={selectedOrders.length > 0 || pendingPresetActive ? total : null}
            selectingMatching={selectingMatching}
            writesAllowed={writesAllowed}
            onAcknowledge={() => setBulkAckOpen(true)}
            onClear={clearSelection}
            onSelectMatching={() => void onSelectMatching()}
          />
        ) : null}

        {bulkAckOpen ? (
          <BulkAckDialog
            open={bulkAckOpen}
            orders={selectedOrders}
            onOpenChange={setBulkAckOpen}
            onFinished={() => {
              clearSelection()
              void query.refetch()
            }}
          />
        ) : null}

        {query.isError && query.data && (
          <p className="text-[12px] text-[color:var(--warn)]">
            The last refresh failed — showing the previous page while we retry.
          </p>
        )}
      </section>
    </TooltipProvider>
  )
}

function ErrorPanel({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const message = friendlyQueueError(error)
  return (
    <div
      role="alert"
      className="flex items-start justify-between gap-3 rounded-xl bg-[color:var(--crit-soft)] p-3"
    >
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-4 text-[color:var(--crit)]" />
        <div>
          <div className="font-medium text-[color:var(--crit)]">Queue failed to load</div>
          <div className="text-[12px] text-[color:var(--crit)]/80">{message}</div>
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="mr-1.5 size-3.5" /> Retry
      </Button>
    </div>
  )
}

function EmptyQueue({ channelScoped }: { channelScoped: boolean }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
      <PackageSearch className="size-8 text-[color:var(--muted-foreground)]" />
      <div className="font-medium text-[color:var(--foreground)]">Nothing to work through.</div>
      <div className="max-w-sm text-[12.5px] text-[color:var(--muted-foreground)]">
        {channelScoped ? 'No purchase orders yet for these channels.' : 'No purchase orders yet.'}
      </div>
    </div>
  )
}

/**
 * CSV column set. Matches the on-screen columns (item 6) with a small
 * useful trailer — line count, ordered units, and ordered value — that ops
 * asked for on-and-off in the legacy Pulse export.
 */
const CSV_COLUMNS: CsvCol<Order>[] = [
  { label: 'PO number', value: order => order.externalOrderNumber },
  { label: 'Channel', value: order => channelLabel(order.channel) },
  { label: 'Ship-to', value: order => order.shipToLocationCode ?? '' },
  { label: 'PO date', value: order => (order.orderedAt ? fullDate(order.orderedAt) : '') },
  { label: 'Landed in Pulse', value: order => fullDate(order.createdAt) },
  { label: 'Status', value: order => STATUS_LABEL[order.status] ?? order.status },
  { label: 'Vendor', value: order => vendorLabel(order) },
  { label: 'Lines', value: order => order.items.length },
  {
    label: 'Units ordered',
    value: order => poListFulfilment(order).orderedQty,
  },
  {
    label: 'Value (INR)',
    value: order => Math.round(poListFulfilment(order).orderedValueRupees),
  },
  {
    label: 'Sent to warehouse units',
    value: order => poListFulfilment(order).sentQty ?? '',
  },
  {
    label: 'Sent to warehouse value (INR)',
    value: order => {
      const value = poListFulfilment(order).sentValueRupees
      return value == null ? '' : Math.round(value)
    },
  },
  {
    label: 'Pending units',
    value: order => poListFulfilment(order).remainingQty,
  },
  {
    label: 'Pending value (INR)',
    value: order => Math.round(poListFulfilment(order).remainingValueRupees),
  },
]

function EmptyFiltered({ onClear }: { onClear: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
      <PackageSearch className="size-8 text-[color:var(--muted-foreground)]" />
      <div className="font-medium text-[color:var(--foreground)]">No matches.</div>
      <div className="text-[12.5px] text-[color:var(--muted-foreground)]">
        Nothing lines up with the current filters.
      </div>
      <Button variant="outline" size="sm" onClick={onClear}>
        Clear filters
      </Button>
    </div>
  )
}
